import {
  Box,
  TextField,
  Switch,
  FormGroup,
  FormControlLabel,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import React, { useContext, useEffect, useState } from "react";
import { AddressContext, getNewAddress } from "./AddressContext";
import { StateEnum, CountryEnum } from "src/common/enum";
import { useForm, useWatch } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  saveAddress,
  editAddress,
  deleteAddress
} from "./AddressService";
import { useDataProvider, useNotify, useRecordContext, useRefresh } from "react-admin";
import { AddressFormAutocomplete } from "./AddressFormAutocomplete";
import { AddressFormTelInput } from "./AddressFormTelInput";

const AddressForm = (props) => {
  const { ...rest } = props;
  const { selected } = useContext(AddressContext);
  const [formData, setFormData] = useState(selected);
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const record = useRecordContext();

  const { trigger, register, control, reset, clearErrors, setError, getValues, setValue, formState: { errors }} = useForm({ 
    defaultValues: getNewAddress(),
    values: formData
  });

  const country = useWatch<{ country: string }>({control, name: 'country' });

  useEffect(() => {
    reset();
    clearErrors();
    if (typeof selected !== "undefined") {
      setFormData(selected);
    }
  }, [selected]);

  useEffect(() => {
    setValue('state', '');
  }, [country]);

  const addressFunctionProps = {
    previousData: selected,
    data: getValues(),
    dataProvider: dataProvider,
    id: record.id,
  };

  const handleSubmit = () => {
    trigger().then(validated => {
      if (validated) {
        setLoading(true);
        if (+formData.id === 0 || typeof formData.id === "undefined") {
          saveAddress(addressFunctionProps).then(res => {
            notify(`Address has been added`, { type: 'success' });
            setLoading(false); 
            refresh();
          }).catch(err => {
            notify(err.message, { type: 'error' });
            if (err.body?.errors) {
              for (const [key, value] of Object.entries(err.body?.errors)) {
                setError(key, { type: 'custom', message: value as string });
              }
            }

            setLoading(false); 
          });
        } else {
          editAddress(addressFunctionProps).then(res => {
            notify(`Address has been edited`, { type: 'success' });
            setLoading(false); 
            refresh();
          }).catch((err) => {
            notify(err.message, { type: 'error' });
            if (err.body?.errors) {
              for (const [key, value] of Object.entries(err.body?.errors)) {
                setError(key, { type: 'custom', message: value as string });
              }
            }

            setLoading(false); 
          });
        }       
      }
    });
  };

  const handleDelete = () => {
    deleteAddress(formData.id, dataProvider).then(res => {
      notify(`Address has been removed`, { type: 'success' });
      setLoading(false); 
      setDeleteModalOpened(false);
      refresh();
    }).catch((err) => {
      notify(err.message, { type: 'error' });
      setLoading(false); 
    });
  }

  return (
    <Box {...rest}>
      
      <Box display="flex">
        <TextField
          label="First name"
          variant="outlined"
          error={!!errors.first_name}
          helperText={(errors.first_name as any)?.message}
          {...register("first_name", {
            required: "Field is required"
          })}
          sx={fieldStyle}
          onChange={(e) => setValue("first_name", e.target?.value, { shouldValidate: true })}
          value={getValues("first_name") || ""}
        />
        <TextField
          label="Last name"
          variant="outlined"
          error={!!errors.last_name}
          helperText={(errors.last_name as any)?.message}
          {...register("last_name", {
            required: "Field is required"
          })}
          sx={fieldStyle}
          onChange={(e) => setValue("last_name", e.target?.value, { shouldValidate: true })}
          value={getValues("last_name") || ""}
        />
      </Box>

      <Box display="flex">
        <TextField
          label="Email"
          variant="outlined"
          error={!!errors.email}
          helperText={(errors.email as any)?.message}
          {...register("email")}
          sx={fieldStyle}
          onChange={(e) => setValue("email", e.target?.value, { shouldValidate: true })}
          value={getValues("email") || ""}
        />
        <TextField
          label="Company"
          variant="outlined"
          error={!!errors.company}
          helperText={(errors.company as any)?.message}
          {...register("company")}
          sx={fieldStyle}
          onChange={(e) => setValue("company", e.target?.value, { shouldValidate: true })}
          value={getValues("company") || ""}
        />
      </Box>

      <Box display="flex">
        <TextField
          label="Street Address"
          variant="outlined"
          error={!!errors.street_address}
          helperText={(errors.street_address as any)?.message}
          {...register("street_address", {
            required: "Field is required"
          })}
          sx={fieldStyle}
          onChange={(e) => setValue("street_address", e.target?.value, { shouldValidate: true })}
          value={getValues("street_address") || ""}
        />
        <TextField
          label="City"
          variant="outlined"
          error={!!errors.city}
          helperText={(errors.city as any)?.message}
          {...register("city", {
            required: "Field is required"
          })}
          sx={fieldStyle}
          onChange={(e) => setValue("city", e.target?.value, { shouldValidate: true })}
          value={getValues("city") || ""}
        />
        <TextField
          label="Zip"
          variant="outlined"
          error={!!errors.zip}
          helperText={(errors.zip as any)?.message}
          {...register("zip", {
            required: "Field is required"
          })}
          sx={fieldStyle}
          onChange={(e) => setValue("zip", e.target?.value, { shouldValidate: true })}
          value={getValues("zip") || ""}
        />
      </Box>

      <Box display="flex">
        <AddressFormAutocomplete
          label={"Country"}
          error={!!errors.country}
          helperText={(errors.country as any)?.message}
          {...register("country", {
            required: "Field is required"
          })}
          onChange={(e, value) =>
            value === null ? "" : setValue("country", value.id, { shouldValidate: true })
          }
          options={CountryEnum}
          size="small"
          style={autocompleteStyle}
          value={getValues("country") || ""}
        />

        { country == 'US' ?
          <AddressFormAutocomplete
            error={!!errors.state}
            helperText={(errors.state as any)?.message}
            {...register("state")}
            label={"State"}
            onChange={(e, value) =>
              value === null ? "" : setValue("state", value.id, { shouldValidate: true })
            }
            size="small"
            style={autocompleteStyle}
            options={StateEnum}
            value={getValues("state") || formData.state || ""}
          />
        :
          <TextField
            label="State / Region"
            variant="outlined"
            error={!!errors.state}
            helperText={(errors.state as any)?.message}
            {...register("state", {
              required: false
            })}
            sx={fieldStyle}
            onChange={(e) => setValue("state", e.target?.value, { shouldValidate: true })}
            value={getValues("state") || formData.state || ""}
          />
        }
      </Box>

      <Box display="flex">
        <AddressFormTelInput
          label="Phone"
          error={!!errors.phone}
          helperText={(errors.phone as any)?.message}
          {...register("phone", {
            required: "Field is required"
          })}
          onChange={(value) => setValue("phone", value, { shouldValidate: true })}
          stackStyle={telInputStyle}
          required
          value={getValues("phone") || ""}
        />
        <AddressFormTelInput
          label="Mobile"
          error={!!errors.mobile}
          helperText={(errors.mobile as any)?.message}
          {...register("mobile", {required: false})}
          stackStyle={telInputStyle}
          onChange={(value) => setValue("mobile", value, { shouldValidate: true })}
          value={getValues("mobile") || ""}
        />
      </Box>

      <FormGroup>
        <Box display="flex" margin="10px">
          <FormControlLabel
            control={
              <Switch
                checked={getValues('default_billing') || false}
                onChange={(e) => setValue("default_billing", e.target.checked, { shouldValidate: true })}
              />
            }
            label="Default billing"
          />
          <FormControlLabel
            control={
              <Switch
                checked={getValues('default_shipping') || false}
                onChange={(e) => setValue("default_shipping", e.target.checked, { shouldValidate: true })}
              />
            }
            label="Default shipping"
          />
        </Box>
      </FormGroup>

      <Box display="flex" justifyContent="space-between" margin="10px">
        {+formData.id === 0 || typeof formData.id === "undefined" ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleSubmit}
          >
            {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
            Add
          </Button>
        ) : (
          <>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleSubmit}
          >
            {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
            Update
          </Button>
          <Button 
            color="error" 
            startIcon={<DeleteIcon />} 
            variant="text" 
            onClick={() => setDeleteModalOpened(true)}
            disabled={getValues('default_billing') || getValues('default_shipping')}
          >
            Delete
          </Button>
          <Dialog
            open={deleteModalOpened}
            onClose={() => setDeleteModalOpened(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Delete Address
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to remove this address?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteModalOpened(false)}>
                No
              </Button>
              <Button onClick={handleDelete} autoFocus>
                Yes
              </Button>
            </DialogActions>
          </Dialog>
          </>
        )}
      </Box>
      
    </Box>
  );
};

const fieldStyle = {
  margin: "12px",
  flex: "1",
  input: {
    padding: "8.5px 14px"
  },
  label: { top: "-6px" },
  "label.MuiInputLabel-shrink": { top: "0px" },
};

const autocompleteStyle = 
{
  margin: "12px",
  flex: "1",
  padding: "0",
};

const telInputStyle = 
{
  margin: "12px",
  padding: "0",
};


export default AddressForm;
